import {
  apiClient,
} from "./apiClient";

import type {
  AdminOrdersQuery,
  OrderDetailsData,
  OrderStatusHistoryData,
  OrderTimelineData,
  OrderTimelineQuery,
  OrdersListData,
  RawOrdersListData,
  UpdateOrderAddressInput,
  UpdateOrderItemsInput,
  UpdateOrderPaymentStatusInput,
  UpdateOrderStatusInput,
} from "../types/orders-admin.types";

function buildQuery<
  T extends object
>(
  values: T,
): string {
  const params =
    new URLSearchParams();

  Object.entries(
    values,
  ).forEach(
    ([
      key,
      value,
    ]) => {
      if (
        value ===
          undefined ||
        value ===
          null ||
        value ===
          ""
      ) {
        return;
      }

      params.set(
        key,
        String(
          value,
        ),
      );
    },
  );

  const string =
    params.toString();

  return string
    ? `?${string}`
    : "";
}

function normalizeCollection<
  T
>(
  result: any,
): {
  items: T[];
  count: number;
} {
  if (
    Array.isArray(
      result,
    )
  ) {
    return {
      items:
        result as T[],

      count:
        result.length,
    };
  }

  const items =
    Array.isArray(
      result?.items,
    )
      ? result.items
      : Array.isArray(
            result?.orders,
          )
        ? result.orders
        : Array.isArray(
              result?.history,
            )
          ? result.history
          : Array.isArray(
                result
                  ?.timeline,
              )
            ? result.timeline
            : [];

  return {
    items,

    count:
      Number(
        result?.count ??
          result
            ?.totalMatched ??
          items.length,
      ),
  };
}

export const ordersAdminApi = {
  /**
   * =========================================================
   * GET /orders
   * =========================================================
   */

  async list(
    query:
      AdminOrdersQuery =
        {},
  ): Promise<
    OrdersListData
  > {
    const result =
      await apiClient.get<
        RawOrdersListData
      >(
        `/orders${buildQuery(
          query,
        )}`,
      );

    const orders =
      result.orders ??
      result.items ??
      [];

    return {
      orders,

      count:
        result.count ??
        result.totalMatched ??
        orders.length,

      pagination:
        result.pagination ??
        {
          limit:
            query.limit ??
            30,

          hasMore:
            result.hasMore ??
            false,

          nextCursor:
            result.nextCursor ??
            null,
        },
    };
  },

  /**
   * =========================================================
   * GET /orders/:orderId
   * =========================================================
   */

  getById(
    orderId: string,
  ) {
    return apiClient.get<
      OrderDetailsData
    >(
      `/orders/${encodeURIComponent(
        orderId,
      )}`,
    );
  },

  /**
   * =========================================================
   * GET /orders/:orderId/status-history
   * =========================================================
   */

  async getStatusHistory(
    orderId: string,
  ): Promise<
    OrderStatusHistoryData
  > {
    const result =
      await apiClient.get<any>(
        `/orders/${encodeURIComponent(
          orderId,
        )}/status-history`,
      );

    return normalizeCollection(
      result,
    );
  },

  /**
   * =========================================================
   * GET /orders/:orderId/timeline
   * =========================================================
   */

async getTimeline(
  orderId: string,
  query:
    OrderTimelineQuery =
      {},
): Promise<
  OrderTimelineData
> {
  const result =
    await apiClient.get<any>(
      `/orders/${encodeURIComponent(
        orderId,
      )}/timeline${buildQuery(
        query,
      )}`,
    );

  const normalized =
    normalizeCollection<
      import("../types/orders-admin.types")
        .OrderTimelineItem
    >(
      result,
    );

  return {
    items:
      normalized.items,

    count:
      normalized.count,

    pagination:
      result?.pagination,
  };
},

  /**
   * =========================================================
   * PATCH /orders/:orderId/status
   * =========================================================
   */

  updateStatus(
    orderId: string,
    input:
      UpdateOrderStatusInput,
  ) {
    return apiClient.patch<
      OrderDetailsData
    >(
      `/orders/${encodeURIComponent(
        orderId,
      )}/status`,
      input,
    );
  },

  /**
   * =========================================================
   * PATCH /orders/:orderId/payment-status
   * =========================================================
   */

  updatePaymentStatus(
    orderId: string,
    input:
      UpdateOrderPaymentStatusInput,
  ) {
    return apiClient.patch<
      OrderDetailsData
    >(
      `/orders/${encodeURIComponent(
        orderId,
      )}/payment-status`,
      input,
    );
  },

  /**
   * =========================================================
   * PATCH /orders/:orderId/address
   * =========================================================
   */

  updateAddress(
    orderId: string,
    input:
      UpdateOrderAddressInput,
  ) {
    return apiClient.patch<
      OrderDetailsData
    >(
      `/orders/${encodeURIComponent(
        orderId,
      )}/address`,
      input,
    );
  },

  /**
   * =========================================================
   * PATCH /orders/:orderId/items
   * =========================================================
   */

  updateItems(
    orderId: string,
    input:
      UpdateOrderItemsInput,
  ) {
    return apiClient.patch<
      OrderDetailsData
    >(
      `/orders/${encodeURIComponent(
        orderId,
      )}/items`,
      input,
    );
  },
};